import { useMemo } from 'react'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { invokeEdgeFunction } from '@/lib/supabaseEdge'
import { STATION_FIELDS, MANAGER_FIELDS } from '@/big-political-list/lib/stationUtils'

const BPL_STATIONS_KEY = ['bpl', 'stations']
const BPL_MANAGERS_KEY = ['bpl', 'repFirmManagers']
const BPL_CHANGE_LOGS_KEY = ['bpl', 'changeLogs']
const BPL_IMPORT_HISTORY_KEY = ['bpl', 'importHistory']
const BPL_COUNTS_KEY = ['bpl', 'counts']

const STATIONS_PAGE_SIZE = 1000

const keepPrevious = (previousData) => previousData

function pickFields(row, fields) {
  const out = {}
  fields.forEach((f) => {
    out[f] = row[f] ?? ''
  })
  return out
}

async function fetchStationsPage(pageParam) {
  const from = pageParam
  const { data, error } = await supabase
    .from('bpl_stations')
    .select('*')
    .order('created_at', { ascending: false })
    .range(from, from + STATIONS_PAGE_SIZE - 1)
  if (error) throw error
  return data ?? []
}

export function useStations() {
  const query = useInfiniteQuery({
    queryKey: BPL_STATIONS_KEY,
    queryFn: ({ pageParam }) => fetchStationsPage(pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < STATIONS_PAGE_SIZE) return undefined
      return allPages.reduce((sum, p) => sum + p.length, 0)
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPrevious,
  })

  const stations = useMemo(
    () => query.data?.pages.flat() ?? [],
    [query.data]
  )

  const isLoadingMore = query.isFetchingNextPage
  const hasMore = query.hasNextPage ?? false

  return {
    ...query,
    data: stations,
    isLoadingMore,
    hasMore,
    loadProgressLabel:
      query.isPending && stations.length === 0
        ? 'Loading stations…'
        : isLoadingMore || hasMore
          ? `Loaded ${stations.length.toLocaleString()} stations${isLoadingMore ? ' (loading more…)' : ''}`
          : null,
  }
}

export function useRepFirmManagers() {
  return useQuery({
    queryKey: BPL_MANAGERS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bpl_rep_firm_managers')
        .select('*')
        .order('rep_firm', { ascending: true })
      if (error) throw error
      return data ?? []
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPrevious,
  })
}

export function useBplCounts() {
  return useQuery({
    queryKey: BPL_COUNTS_KEY,
    queryFn: async () => {
      const [stationsRes, managersRes] = await Promise.all([
        supabase.from('bpl_stations').select('*', { count: 'exact', head: true }),
        supabase.from('bpl_rep_firm_managers').select('*', { count: 'exact', head: true }),
      ])
      if (stationsRes.error) throw stationsRes.error
      if (managersRes.error) throw managersRes.error
      return {
        stationCount: stationsRes.count ?? 0,
        managerCount: managersRes.count ?? 0,
      }
    },
    staleTime: 60 * 1000,
  })
}

export function useChangeLogs() {
  return useQuery({
    queryKey: BPL_CHANGE_LOGS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bpl_change_log_entries')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(500)
      if (error) throw error
      return data ?? []
    },
    staleTime: 30 * 1000,
    placeholderData: keepPrevious,
  })
}

export function useImportHistory() {
  return useQuery({
    queryKey: BPL_IMPORT_HISTORY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bpl_import_history')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(100)
      if (error) throw error
      return data ?? []
    },
    staleTime: 60 * 1000,
  })
}

async function throwIfError(promise) {
  const result = await promise
  if (result.error) throw result.error
  return result.data
}

export function useCreateStation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) =>
      throwIfError(
        supabase.from('bpl_stations').insert(pickFields(data, STATION_FIELDS)).select().single()
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BPL_STATIONS_KEY })
      qc.invalidateQueries({ queryKey: BPL_COUNTS_KEY })
    },
  })
}

export function useUpdateStation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }) =>
      throwIfError(
        supabase
          .from('bpl_stations')
          .update(pickFields(data, STATION_FIELDS))
          .eq('id', id)
          .select()
          .single()
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: BPL_STATIONS_KEY }),
  })
}

export function useDeleteStation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => throwIfError(supabase.from('bpl_stations').delete().eq('id', id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BPL_STATIONS_KEY })
      qc.invalidateQueries({ queryKey: BPL_COUNTS_KEY })
    },
  })
}

export function useCreateManager() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) =>
      throwIfError(
        supabase
          .from('bpl_rep_firm_managers')
          .insert(pickFields(data, MANAGER_FIELDS))
          .select()
          .single()
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BPL_MANAGERS_KEY })
      qc.invalidateQueries({ queryKey: BPL_COUNTS_KEY })
    },
  })
}

export function useUpdateManager() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }) =>
      throwIfError(
        supabase
          .from('bpl_rep_firm_managers')
          .update(pickFields(data, MANAGER_FIELDS))
          .eq('id', id)
          .select()
          .single()
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: BPL_MANAGERS_KEY }),
  })
}

export function useDeleteManager() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => throwIfError(supabase.from('bpl_rep_firm_managers').delete().eq('id', id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BPL_MANAGERS_KEY })
      qc.invalidateQueries({ queryKey: BPL_COUNTS_KEY })
    },
  })
}

export function useCreateChangeLog() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) =>
      throwIfError(
        supabase.from('bpl_change_log_entries').insert({
          ...data,
          timestamp: data.timestamp ?? new Date().toISOString(),
          field_changes: data.field_changes ?? [],
          summary: data.summary ?? '',
        })
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: BPL_CHANGE_LOGS_KEY }),
  })
}

export function useCreateImportHistory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) =>
      throwIfError(
        supabase.from('bpl_import_history').insert({
          ...data,
          timestamp: data.timestamp ?? new Date().toISOString(),
          errors: data.errors ?? [],
        })
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: BPL_IMPORT_HISTORY_KEY }),
  })
}

export async function uploadBplImportFile(file) {
  const ext = file.name.split('.').pop() || 'bin'
  const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
  const { error } = await supabase.storage.from('bpl-imports').upload(path, file, {
    upsert: false,
  })
  if (error) throw error
  return path
}

export async function parseBplImportFile(storagePath) {
  const res = await invokeEdgeFunction('bpl-parse-excel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ storage_path: storagePath }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.error || 'Parse failed')
  return json
}

export async function confirmBplImport({ stations, managers, mode, filename, user, confirmWipeStations }) {
  const res = await invokeEdgeFunction('bpl-import-confirm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stations, managers, mode, filename, user, confirmWipeStations }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.error || 'Import failed')
  return json
}

export async function purgeBplData() {
  const res = await invokeEdgeFunction('bpl-purge-data', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm: 'DELETE ALL' }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.error || 'Purge failed')
  return json
}

export const bplQueryKeys = {
  stations: BPL_STATIONS_KEY,
  managers: BPL_MANAGERS_KEY,
  changeLogs: BPL_CHANGE_LOGS_KEY,
  importHistory: BPL_IMPORT_HISTORY_KEY,
  counts: BPL_COUNTS_KEY,
}

// ─── Station Share ────────────────────────────────────────────────────────────

const BPL_SS_MARKETS_KEY = ['bpl', 'ssMarkets']
const BPL_SS_ENTRIES_KEY = ['bpl', 'ssEntries']
const BPL_SS_NOTES_KEY   = ['bpl', 'ssNotes']

// Fetch all rows from a Supabase table by paging in chunks of PAGE_SIZE.
// PostgREST enforces a server-side max-rows ceiling (default 1 000) that
// client-side .limit() cannot exceed, so a single request always truncates
// datasets larger than that ceiling.
async function fetchAllRows(builder, pageSize = 1000) {
  const all = []
  let from = 0
  while (true) {
    const { data, error } = await builder(from, from + pageSize - 1)
    if (error) throw error
    all.push(...(data ?? []))
    if (!data || data.length < pageSize) break
    from += pageSize
  }
  return all
}

export function useStationShareMarkets() {
  return useQuery({
    queryKey: BPL_SS_MARKETS_KEY,
    queryFn: () =>
      fetchAllRows((from, to) =>
        supabase
          .from('bpl_station_share_markets')
          .select('*')
          .order('state', { ascending: true })
          .range(from, to)
      ),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPrevious,
  })
}

export function useStationShareEntries() {
  return useQuery({
    queryKey: BPL_SS_ENTRIES_KEY,
    queryFn: () =>
      fetchAllRows((from, to) =>
        supabase
          .from('bpl_station_share_entries')
          .select('*')
          .order('sort_order', { ascending: true })
          .range(from, to)
      ),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPrevious,
  })
}

export function useStationShareNotes() {
  return useQuery({
    queryKey: BPL_SS_NOTES_KEY,
    queryFn: () =>
      fetchAllRows((from, to) =>
        supabase
          .from('bpl_station_share_notes')
          .select('*')
          .order('created_at', { ascending: false })
          .range(from, to)
      ),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPrevious,
  })
}

export function useUpdateStationShareMarket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }) =>
      throwIfError(
        supabase
          .from('bpl_station_share_markets')
          .update(data)
          .eq('id', id)
          .select()
          .single()
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: BPL_SS_MARKETS_KEY }),
  })
}

export function useCreateStationShareEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) =>
      throwIfError(
        supabase.from('bpl_station_share_entries').insert(data).select().single()
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: BPL_SS_ENTRIES_KEY }),
  })
}

export function useUpdateStationShareEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }) =>
      throwIfError(
        supabase
          .from('bpl_station_share_entries')
          .update(data)
          .eq('id', id)
          .select()
          .single()
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: BPL_SS_ENTRIES_KEY }),
  })
}

export function useDeleteStationShareEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) =>
      throwIfError(supabase.from('bpl_station_share_entries').delete().eq('id', id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: BPL_SS_ENTRIES_KEY }),
  })
}

export function useCreateStationShareNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) =>
      throwIfError(
        supabase.from('bpl_station_share_notes').insert(data).select().single()
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: BPL_SS_NOTES_KEY }),
  })
}

export function useDeleteStationShareNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) =>
      throwIfError(supabase.from('bpl_station_share_notes').delete().eq('id', id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: BPL_SS_NOTES_KEY }),
  })
}

// ─── Station Share import helpers ────────────────────────────────────────────

/**
 * Parse a token like "KYUR-12%" or "**WBRC-39%**" into an entry object.
 * affiliateMap is an optional { [CALLSIGN_UPPER]: affiliate } lookup.
 * Returns null if the token does not match the expected CALLSIGN-NN% pattern.
 */
function parseShareToken(token, sortOrder, affiliateMap = {}) {
  let deal = false
  let t = token.trim()
  if (t.startsWith('**') && t.endsWith('**')) {
    deal = true
    t = t.slice(2, -2)
  }
  // Split on last hyphen before the trailing %
  const pctIdx = t.lastIndexOf('%')
  if (pctIdx === -1) return null
  const withoutPct = t.slice(0, pctIdx)
  const hyphenIdx = withoutPct.lastIndexOf('-')
  if (hyphenIdx === -1) return null
  const callSign = withoutPct.slice(0, hyphenIdx).trim()
  const shareStr = withoutPct.slice(hyphenIdx + 1).trim()
  const share = parseFloat(shareStr)
  if (!callSign || isNaN(share)) return null
  const affiliate = affiliateMap[callSign.toUpperCase()] ?? ''
  return { call_sign: callSign, affiliate, share, sort_order: sortOrder, deal }
}

/**
 * Parse raw rows [{State, Market, Shares}, ...] from a spreadsheet into
 * structured { markets, entries, notes } arrays ready for bulk insert.
 * affiliateMap is an optional { [CALLSIGN_UPPER]: affiliate } lookup built
 * from bpl_stations so each entry gets its network auto-filled on import.
 */
export function parseStationShareRows(rawRows, affiliateMap = {}) {
  // market key → { state, market, entries[], notes[] }
  const marketMap = new Map()

  for (const row of rawRows) {
    const state = (row['State'] ?? row['state'] ?? '').toString().trim()
    const market = (row['Market'] ?? row['market'] ?? '').toString().trim()
    const sharesRaw = (row['Shares'] ?? row['shares'] ?? '').toString().trim()

    if (!state || !market) continue

    const key = `${state}|||${market}`
    if (!marketMap.has(key)) {
      marketMap.set(key, { state, market, entries: [], notes: [] })
    }
    const rec = marketMap.get(key)

    // Extract trailing buy-rule note: text inside last (…) at end
    let shares = sharesRaw
    const noteMatch = shares.match(/\s*\((.+)\)\s*$/)
    if (noteMatch) {
      rec.notes.push({ kind: 'buy_rule', body: noteMatch[1].trim(), author: 'Import' })
      shares = shares.slice(0, shares.lastIndexOf('(')).trim()
    }

    // Parse individual station tokens
    const tokens = shares.split(/\s+/).filter(Boolean)
    let sortOrder = rec.entries.length + 1
    for (const token of tokens) {
      const entry = parseShareToken(token, sortOrder, affiliateMap)
      if (entry) {
        rec.entries.push(entry)
        sortOrder++
      }
    }
  }

  // Build flat arrays with share_sum on each market
  const markets = []
  const entries = []
  const notes = []

  for (const rec of marketMap.values()) {
    const shareSum = Math.round(rec.entries.reduce((s, e) => s + e.share, 0))
    markets.push({
      state: rec.state,
      market: rec.market,
      share_sum: shareSum,
      updated_by: 'Import',
    })
    // entries and notes are indexed by market array position — resolved after insert
    entries.push(rec.entries)
    notes.push(rec.notes)
  }

  return { markets, entries, notes }
}

/**
 * Bulk-insert parsed station share data into Supabase.
 * mode = 'append' | 'replace'
 * Returns { marketCount, entryCount, noteCount }
 */
export async function importStationShareData(parsed, mode, qc, userName = 'User', filename = 'import') {
  const { markets, entries, notes } = parsed

  if (mode === 'replace') {
    // Cascade deletes entries + notes automatically
    const { error } = await supabase
      .from('bpl_station_share_markets')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000')
    if (error) throw error
  }

  let marketsToInsert = markets
  let entryGroups = entries
  let noteGroups = notes

  if (mode === 'append') {
    // Fetch existing (state, market) pairs to skip duplicates
    const { data: existing, error: fetchErr } = await supabase
      .from('bpl_station_share_markets')
      .select('state, market')
    if (fetchErr) throw fetchErr
    const existingKeys = new Set((existing ?? []).map(r => `${r.state}|||${r.market}`))
    const filtered = []
    const filteredEntries = []
    const filteredNotes = []
    for (let i = 0; i < markets.length; i++) {
      const key = `${markets[i].state}|||${markets[i].market}`
      if (!existingKeys.has(key)) {
        filtered.push(markets[i])
        filteredEntries.push(entryGroups[i])
        filteredNotes.push(noteGroups[i])
      }
    }
    marketsToInsert = filtered
    entryGroups = filteredEntries
    noteGroups = filteredNotes
  }

  if (marketsToInsert.length === 0) {
    qc.invalidateQueries({ queryKey: BPL_SS_MARKETS_KEY })
    qc.invalidateQueries({ queryKey: BPL_SS_ENTRIES_KEY })
    qc.invalidateQueries({ queryKey: BPL_SS_NOTES_KEY })
    return { marketCount: 0, entryCount: 0, noteCount: 0 }
  }

  // Insert markets in bulk; get back ids in order
  const { data: insertedMarkets, error: mErr } = await supabase
    .from('bpl_station_share_markets')
    .insert(marketsToInsert)
    .select('id')
  if (mErr) throw mErr

  // Build flat entries + notes arrays with resolved market_id
  const allEntries = []
  const allNotes = []
  for (let i = 0; i < insertedMarkets.length; i++) {
    const marketId = insertedMarkets[i].id
    for (const e of entryGroups[i]) {
      allEntries.push({ ...e, market_id: marketId })
    }
    for (const n of noteGroups[i]) {
      allNotes.push({ ...n, market_id: marketId, created_at: new Date().toISOString() })
    }
  }

  // Insert in chunks — PostgREST silently truncates single requests > 1000 rows
  const CHUNK = 500
  for (let i = 0; i < allEntries.length; i += CHUNK) {
    const { error: eErr } = await supabase
      .from('bpl_station_share_entries')
      .insert(allEntries.slice(i, i + CHUNK))
    if (eErr) throw eErr
  }

  for (let i = 0; i < allNotes.length; i += CHUNK) {
    const { error: nErr } = await supabase
      .from('bpl_station_share_notes')
      .insert(allNotes.slice(i, i + CHUNK))
    if (nErr) throw nErr
  }

  const marketCount = insertedMarkets.length
  const entryCount = allEntries.length
  const noteCount = allNotes.length

  // Write to change log
  await supabase.from('bpl_change_log_entries').insert({
    user: userName,
    action: 'imported',
    entity_type: 'StationShare',
    record_label: `${marketCount} markets, ${entryCount} entries`,
    summary: `Station Share imported (${mode})`,
    field_changes: [],
  })

  // Write to import history (station_count = markets, manager_count = entries)
  await supabase.from('bpl_import_history').insert({
    filename,
    user: userName,
    station_count: marketCount,
    manager_count: entryCount,
    status: 'success',
    import_type: 'station_share',
    errors: [],
  })

  qc.invalidateQueries({ queryKey: BPL_SS_MARKETS_KEY })
  qc.invalidateQueries({ queryKey: BPL_SS_ENTRIES_KEY })
  qc.invalidateQueries({ queryKey: BPL_SS_NOTES_KEY })
  qc.invalidateQueries({ queryKey: BPL_CHANGE_LOGS_KEY })
  qc.invalidateQueries({ queryKey: BPL_IMPORT_HISTORY_KEY })

  return { marketCount, entryCount, noteCount }
}

// ─── Affiliate reference helpers ─────────────────────────────────────────────

/**
 * Parse rows from the "Station-Affiliate-Market sheet" Excel into a lookup map.
 * Accepts column names: Station / Call Sign / call_sign, Affiliate / affil, Market / market.
 * Returns { [CALLSIGN_UPPER]: affiliate } (e.g. { 'KXAS': 'NBC' }).
 */
export function parseAffiliateSheetRows(rawRows) {
  const map = {}
  for (const row of rawRows) {
    const callSign = (
      row['Station'] ?? row['Call Sign'] ?? row['call_sign'] ?? ''
    ).toString().trim()
    const affiliate = (
      row['Affiliate'] ?? row['affil'] ?? row['AFFIL'] ?? ''
    ).toString().trim()
    if (callSign && affiliate) {
      map[callSign.toUpperCase()] = affiliate
    }
  }
  return map
}

/**
 * Batch-update affiliates using the lookup map built from the affiliate Excel.
 *
 * Primary: updates bpl_station_share_entries.affiliate by call_sign so
 *          existing imported entries are populated immediately.
 * Secondary: updates bpl_stations.affiliate by station name so future
 *            Station Share imports can look up from bpl_stations.
 *
 * Returns { entriesUpdated, stationsUpdated }.
 */
export async function updateStationAffiliates(affiliateMap) {
  if (Object.keys(affiliateMap).length === 0) return { entriesUpdated: 0, stationsUpdated: 0 }

  // Group call signs by affiliate value (NBC, CBS, ABC, FOX…).
  // This yields ~10-20 groups instead of 1,797 individual requests.
  const byAffiliate = {}
  for (const [cs, aff] of Object.entries(affiliateMap)) {
    if (!byAffiliate[aff]) byAffiliate[aff] = []
    byAffiliate[aff].push(cs)
  }

  let entriesUpdated = 0
  let stationsUpdated = 0

  // One UPDATE per affiliate for bpl_station_share_entries
  for (const [affiliate, callSigns] of Object.entries(byAffiliate)) {
    const { count, error } = await supabase
      .from('bpl_station_share_entries')
      .update({ affiliate }, { count: 'exact' })
      .in('call_sign', callSigns)
    if (!error && count) entriesUpdated += count
  }

  // One UPDATE per affiliate for bpl_stations
  for (const [affiliate, callSigns] of Object.entries(byAffiliate)) {
    const { count, error } = await supabase
      .from('bpl_stations')
      .update({ affiliate }, { count: 'exact' })
      .in('station', callSigns)
    if (!error && count) stationsUpdated += count
  }

  return { entriesUpdated, stationsUpdated }
}

/**
 * Super-admin wipe: deletes all station share markets (cascades to entries + notes).
 */
export async function purgeStationShareData(qc) {
  const { error } = await supabase
    .from('bpl_station_share_markets')
    .delete()
    .neq('id', '00000000-0000-0000-0000-000000000000')
  if (error) throw error
  qc.invalidateQueries({ queryKey: BPL_SS_MARKETS_KEY })
  qc.invalidateQueries({ queryKey: BPL_SS_ENTRIES_KEY })
  qc.invalidateQueries({ queryKey: BPL_SS_NOTES_KEY })
}
